import React, { useState, useEffect, useRef } from 'react';
import { 
  Code, 
  Link, 
  Shield, 
  Zap, 
  Users, 
  Globe,
  CheckCircle,
  Server,
  Lock,
  ArrowRight,
  Database,
  FileText,
  ChevronDown,
  ChevronUp,

  Network
} from 'lucide-react';

// Reusable Components
const SectionHeader = ({ title, subtitle }) => (
  <div className="text-center mb-12">
    <h2 className="text-4xl font-bold text-gray-900 mb-4">{title}</h2>
    <p className="text-xl text-gray-600 max-w-3xl mx-auto">{subtitle}</p>
  </div>
);

const StandardCard = ({ icon: Icon, title, description }) => (
  <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
        <Icon className="w-6 h-6 text-blue-600" />
      </div>
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>
        <p className="text-gray-600 text-sm">{description}</p>
      </div>
    </div>
  </div>
);

const BenefitItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
      <Icon className="w-5 h-5 text-green-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  </div>
);

const EDITransaction = ({ code, title, description }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
        <span className="text-white font-bold text-sm">{code}</span>
      </div>
      <div>
        <h5 className="font-semibold text-gray-900">{title}</h5>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
  </div>
);

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="mb-8 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-6 h-6 text-blue-600" />}
          <h3 className="text-xl font-bold text-gray-900 text-left">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-6 h-6 text-gray-500" />
        ) : (
          <ChevronDown className="w-6 h-6 text-gray-500" />
        )}
      </button>
      
      <div
        className={`transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}
      >
        <div className="p-6 pt-4 bg-white">
          {children}
        </div>
      </div>
    </div>
  );
};

// Scroll Animation Hook
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
};

// Animated Section Component
const AnimatedSection = ({ children, delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Main Component
const StandardsSection = () => {
  const ediTransactions = [
    {
      code: '204',
      title: 'Motor Carrier Load Tender',
      description: 'Automated load tendering and acceptance workflows'
    },
    {
      code: '210',
      title: 'Freight Details & Invoice',
      description: 'Complete freight billing with automatic reconciliation'
    },
    {
      code: '214',
      title: 'Shipment Status',
      description: 'Real-time tracking updates throughout the journey'
    },
    {
      code: '990',
      title: 'Response to Load Tender',
      description: 'Instant tender acceptance and rejection handling'
    }
  ];

  const ediCapabilities = [
    {
      icon: Users,
      title: 'Seamless Trading Partner Integration',
      description: 'Connect with suppliers, carriers, and customers using the same protocol they already trust'
    },
    {
      icon: Zap,
      title: 'Automated Transaction Processing',
      description: 'From purchase orders to invoices, we handle the full transaction lifecycle'
    },
    {
      icon: Shield,
      title: 'Reduced Errors and Delays',
      description: 'Standardized data formats eliminate manual data entry and miscommunication'
    },
    {
      icon: FileText,
      title: 'Regulatory Compliance',
      description: 'Meet industry requirements with formats accepted by major logistics providers'
    }
  ];

  const whyStandardsMatter = [
    {
      icon: Globe,
      title: 'Future-Proof Technology',
      description: 'Standards evolve with industry needs, ensuring your investment remains valuable'
    },
    {
      icon: Link,
      title: 'Vendor Flexibility',
      description: 'No lock-in; easily work with multiple partners and providers'
    },
    {
      icon: Zap,
      title: 'Faster Implementation',
      description: 'Your team already knows these standards, reducing training time'
    },
    {
      icon: Database,
      title: 'Lower Total Cost',
      description: 'Less custom integration work means lower maintenance costs'
    }
  ];

  const additionalProtocols = [
    {
      icon: Server,
      title: 'Modern APIs',
      description: 'REST & GraphQL for real-time data exchange'
    },
    {
      icon: Database,
      title: 'Industry Formats',
      description: 'Support for sector-specific data schemas'
    },
    {
      icon: Lock,
      title: 'Security Standards',
      description: 'Enterprise-grade encryption and authentication'
    },
  ];

  return (
    <section id="standards" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <SectionHeader 
            title="Built on Industry Standards"
            subtitle="We believe in building solutions that integrate seamlessly with your existing systems and workflows"
          />
        </AnimatedSection>

        {/* Main Introduction */}
        <AnimatedSection delay={100}>
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="text-center">
                <Network className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                <p className="text-lg text-gray-700 leading-relaxed">
                  At SYBR, we leverage proven industry standards that form the backbone of modern 
                  business communications. When you work with us, you're using the same trusted protocols that power global logistics.
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Collapsible Sections */}
        <div className="max-w-5xl mx-auto mb-12">
          <AnimatedSection delay={200}>
            <CollapsibleSection 
              title="EDI: The Foundation of Logistics Communication" 
              icon={Code}
              defaultOpen={false}
            >
              <p className="text-gray-600 mb-6">
                Electronic Data Interchange is the gold standard for business-to-business 
                transactions in North America, particularly in logistics and supply chain management.{' '}
                <a 
                  href="https://x12.org/products/by-industry/supply-chain" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Learn more about X12 & ANSI
                </a>
              </p>

              {/* EDI Transactions */}
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Supported EDI Transactions</h4>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {ediTransactions.map((transaction, index) => (
                  <EDITransaction key={index} {...transaction} />
                ))}
              </div>

              {/* EDI Capabilities */}
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Capabilities</h4>
              <div className="grid md:grid-cols-2 gap-6">
                {ediCapabilities.map((capability, index) => (
                  <StandardCard key={index} {...capability} />
                ))}
              </div>
            </CollapsibleSection>
          </AnimatedSection>

          <AnimatedSection delay={250}>
            <CollapsibleSection 
              title="Why Industry Standards Matter" 
              icon={Shield}
            >
              <p className="text-gray-600 mb-6">
                When we build on established standards rather than proprietary formats, you get:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {whyStandardsMatter.map((benefit, index) => (
                  <BenefitItem key={index} {...benefit} />
                ))}
              </div>
            </CollapsibleSection>
          </AnimatedSection>

          <AnimatedSection delay={300}>
            <CollapsibleSection 
              title="Additional Protocols & Standards" 
              icon={Server}
            >
              <p className="text-gray-600 mb-6">
                Beyond EDI X12, we support additional industry protocols and standards to ensure 
                your systems work together seamlessly:
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                {additionalProtocols.map((protocol, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <protocol.icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">{protocol.title}</h4>
                    <p className="text-sm text-gray-600">{protocol.description}</p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          </AnimatedSection>
        </div>

        {/* Bottom CTA */}
        <AnimatedSection delay={350}>
          <div className="mt-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white text-center shadow-xl">
            <h3 className="text-2xl font-bold mb-4">
              Speak the Same Language as Your Technology Ecosystem
            </h3>
            <p className="text-lg text-blue-100 mb-6 max-w-3xl mx-auto">
              By choosing SYBR, you're choosing a partner who understands and respects the 
              standards that power your industry. No proprietary lock-in, no custom protocols—just 
              proven, reliable technology that works with what you already have.
            </p>
            <a 
              href="https://calendly.com/sybrtms/intro-call"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition font-semibold group shadow-lg"
              target="_blank"
              rel="noopener noreferrer"
            >
              See Our Integration Capabilities
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition" />
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default StandardsSection;